import type { FieldExplainerContent } from './types';

export type AdvancedFireExplainerContext = {
  input: {
    currentAge: number;
    retirementAge: number;
    lifeExpectancy: number;
    taxRate: number;
    preRetirementGrowth: number;
    postRetirementGrowth: number;
    withdrawalRate: number;
    inflationRate: number;
    contributionFrequency: 'monthly' | 'yearly';
    penaltyFreeAge: number;
    bracketCapGross: number;
    bracketCapGrowth: number;
    initial401k: number;
    contribution401k: number;
    initialRoth: number;
    contributionRoth: number;
    initialTaxable: number;
    contributionTaxable: number;
    contributionIncrease: number;
  };
  result: {
    /** Balance carried into the retirement year, before that year's growth and first withdrawal. */
    retirementBalance: number;
    /** Gross first-year withdrawal: withdrawal rate applied to retirementBalance. */
    baseWithdrawal: number;
    /** End-of-year total for the retirement year itself (the row shown in the table). */
    retirementYearEndTotal: number;
    normalizedContribution401k: number;
    lifetimeWithdrawn: number;
    lifetimeShortfall: number;
    endingBalance: number;
    endingAge: number;
    endingBalances: [number, number, number];
    finalYearTarget: number;
    firstRetirementNet: number;
    firstRetirementFrom401k: number;
    firstRetirementFromRoth: number;
    firstRetirementFromTaxable: number;
    /** First age whose gross target could not be fully funded, or null when nothing is missed. */
    depletionAge: number | null;
  };
};

const usd = new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
  maximumFractionDigits: 0
});

const usdCents = new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
  minimumFractionDigits: 2,
  maximumFractionDigits: 2
});

/** Annual 401(k) deferral limit enforced by the shared contribution normalizer. */
const ANNUAL_401K_LIMIT = 23500;

function rate(value: number): string {
  return `${Number(value.toFixed(2))}%`;
}

export function getAdvancedFireFieldExplainers(ctx: AdvancedFireExplainerContext): FieldExplainerContent[] {
  const {
    currentAge,
    retirementAge,
    lifeExpectancy,
    taxRate,
    preRetirementGrowth,
    postRetirementGrowth,
    withdrawalRate,
    inflationRate,
    contributionFrequency,
    penaltyFreeAge,
    bracketCapGross,
    bracketCapGrowth,
    initial401k,
    contribution401k,
    initialRoth,
    contributionRoth,
    initialTaxable,
    contributionTaxable,
    contributionIncrease
  } = ctx.input;

  const {
    retirementBalance,
    baseWithdrawal,
    retirementYearEndTotal,
    normalizedContribution401k,
    lifetimeWithdrawn,
    lifetimeShortfall,
    endingBalance,
    endingAge,
    endingBalances,
    finalYearTarget,
    firstRetirementNet,
    firstRetirementFrom401k,
    firstRetirementFromRoth,
    firstRetirementFromTaxable,
    depletionAge
  } = ctx.result;

  const periodLabel = contributionFrequency === 'monthly' ? 'month' : 'year';
  const periodsPerYear = contributionFrequency === 'monthly' ? 12 : 1;
  const savingYears = Math.max(0, retirementAge - currentAge);
  const drawdownYears = Math.max(0, lifeExpectancy - retirementAge + 1);
  const tableRows = Math.max(0, lifeExpectancy - currentAge + 1);
  const bridgeYears = Math.max(0, penaltyFreeAge - retirementAge);
  const preMultiplier =
    contributionFrequency === 'monthly'
      ? Math.pow(1 + preRetirementGrowth / 100 / 12, 12 * savingYears)
      : Math.pow(1 + preRetirementGrowth / 100, savingYears);
  const preGrowthExpression =
    contributionFrequency === 'monthly'
      ? `(1 + ${preRetirementGrowth / 100}/12)^${12 * savingYears}`
      : `(1 + ${preRetirementGrowth / 100})^${savingYears}`;

  const annual401k = normalizedContribution401k * periodsPerYear;
  const annualRoth = contributionRoth * periodsPerYear;
  const annualTaxable = contributionTaxable * periodsPerYear;
  const entered401kAnnual = contribution401k * periodsPerYear;
  const is401kCapped = entered401kAnnual > ANNUAL_401K_LIMIT;
  const stepUp = 1 + contributionIncrease / 100;
  const lastYear401k = annual401k * Math.pow(stepUp, Math.max(0, savingYears - 1));

  const capAt = (age: number) => bracketCapGross * Math.pow(1 + bracketCapGrowth / 100, Math.max(0, age - currentAge));
  const capAtRetirement = capAt(retirementAge);
  const capAtPenaltyFreeAge = capAt(penaltyFreeAge);

  const penaltyRate = taxRate + 10;
  const inflationYears = Math.max(0, lifeExpectancy - currentAge);
  const endingInTodaysDollars = endingBalance / Math.pow(1 + inflationRate / 100, inflationYears);
  const grownRetirementTotal = retirementBalance * (1 + postRetirementGrowth / 100);

  return [
    {
      id: 'currentAge',
      kind: 'input',
      label: 'Current age',
      plain:
        'The first row of the projection and the origin for every exponent on this page. Contribution step-ups and the 401(k) bracket cap are both indexed from here, not from your retirement year.',
      formula: `saving years   = ${retirementAge} − ${currentAge} = ${savingYears}\ndrawdown years = ${lifeExpectancy} − ${retirementAge} + 1 = ${drawdownYears}\ntable rows     = ${lifeExpectancy} − ${currentAge} + 1 = ${tableRows}`,
      feeds: 'The number of rows in the projection table, the age axis on the chart, and how far the contribution step-up and the bracket cap have compounded by the time withdrawals start.'
    },
    {
      id: 'retirementAge',
      kind: 'input',
      label: 'Retirement age',
      plain:
        'Three things happen in this one year: contributions stop, the growth rate switches from pre-retirement to post-retirement, and the first withdrawal is taken.',
      formula: `age < ${retirementAge}:  grow at ${rate(preRetirementGrowth)}, then add contributions\nage ≥ ${retirementAge}:  grow at ${rate(postRetirementGrowth)}, then withdraw\n\nat age ${retirementAge} exactly: full year of growth\n  + full annual withdrawal, no contribution`,
      feeds: 'The balance the withdrawal rate is applied to, and the age where the total line on the chart stops climbing.',
      assumption: `The retirement year is modeled as a whole year on both sides — a full year of ${rate(postRetirementGrowth)} growth and a full annual withdrawal. Retiring mid-year is not modeled.`
    },
    {
      id: 'lifeExpectancy',
      kind: 'input',
      label: 'Life expectancy',
      plain:
        'The last age modeled. The projection simply stops here — there is no estate calculation, no legacy target, and no required minimum distribution rule applied on the way.',
      formula: `last row = age ${lifeExpectancy}\nwithdrawal years = ${drawdownYears}\nfinal gross target = ${usd.format(baseWithdrawal)} × (1 + ${rate(inflationRate)})^${Math.max(0, lifeExpectancy - retirementAge)}\n  = ${usd.format(finalYearTarget)}`,
      feeds: 'The Ending balance card, the last row of the table, and the right edge of the chart.',
      assumption:
        'Because the target keeps inflating to the last row, pushing this age out a few years is the fastest way to see how much slack the plan actually has.'
    },
    {
      id: 'taxRate',
      kind: 'input',
      label: 'Tax rate',
      plain:
        'One flat rate charged on gross withdrawals from the 401(k) and the taxable account. Roth withdrawals are untaxed, and 401(k) withdrawals before the penalty-free age carry an extra 10 points.',
      formula: `401(k) at/after age ${penaltyFreeAge}: net = gross × (1 − ${rate(taxRate)})\n401(k) before age ${penaltyFreeAge}:   net = gross × (1 − ${rate(penaltyRate)})\ntaxable:                    net = gross × (1 − ${rate(taxRate)})\nRoth:                       net = gross`,
      feeds: 'The Withdrawn (net) column, the three source columns beside it, and the Lifetime withdrawn card.',
      assumption:
        'Tax is paid out of the gross withdrawal, so this rate moves your net income but never the balances on the chart. It also charges the full rate on taxable-account sales rather than capital-gains rates on the gain portion, which overstates the cost of spending that account.'
    },
    {
      id: 'preRetirementGrowth',
      kind: 'input',
      label: 'Pre-retirement growth',
      plain:
        contributionFrequency === 'monthly'
          ? 'Applied to all three balances as twelve monthly steps of one-twelfth this rate, with each month’s deposit added at the end of the month. The 401(k), the Roth, and the taxable account all use this same rate.'
          : 'Applied once a year to all three balances, with that year’s contribution added at year end. The 401(k), the Roth, and the taxable account all use this same rate.',
      formula:
        contributionFrequency === 'monthly'
          ? `each month, per account:\n  balance = balance × (1 + ${rate(preRetirementGrowth)}/12)\n  balance = balance + that month’s deposit\n\nover ${savingYears} years the growth multiplier is\n  (1 + ${rate(preRetirementGrowth)}/12)^${12 * savingYears} = ${preMultiplier.toFixed(3)}×`
          : `each saving year, per account:\n  balance = balance × (1 + ${rate(preRetirementGrowth)})\n  balance = balance + that year’s contribution\n\nover ${savingYears} years the multiplier is\n  (1 + ${rate(preRetirementGrowth)})^${savingYears} = ${preMultiplier.toFixed(3)}×`,
      feeds: 'The balance at retirement, which sets the withdrawal target for every drawdown year that follows.',
      assumption:
        'The same return every year, identical across all three accounts, with no fees, no volatility, and no difference in asset mix between a 401(k) and a brokerage account.'
    },
    {
      id: 'postRetirementGrowth',
      kind: 'input',
      label: 'Post-retirement growth',
      plain:
        'Takes over from the pre-retirement rate in your retirement year. It is applied to all three accounts at the start of each year, and only then is the withdrawal taken.',
      formula: `each retirement year:\n  1. every account × (1 + ${rate(postRetirementGrowth)})\n  2. take that year’s gross target from the accounts\n\nage ${retirementAge}: ${usd.format(retirementBalance)} × (1 + ${rate(postRetirementGrowth)})\n  = ${usd.format(grownRetirementTotal)} before the first withdrawal`,
      feeds: 'Every retirement row in the table, the shape of the total line after retirement, and the ending balance.',
      assumption:
        'Growth is credited before the withdrawal, so the portfolio earns a full year on money it is about to spend. Taking the withdrawal first would leave less at every age.'
    },
    {
      id: 'withdrawalRate',
      kind: 'input',
      label: 'Initial withdrawal rate',
      plain:
        'Sets the first year’s gross withdrawal as a share of the balance at retirement. Every later year is that same dollar amount grown by inflation — it is never recalculated against the current balance.',
      formula: `base = ${rate(withdrawalRate)} × ${usd.format(retirementBalance)}\n     = ${usd.format(baseWithdrawal)}   (gross, age ${retirementAge})\n\nage ${retirementAge + 1} target = base × (1 + ${rate(inflationRate)})^1\nage ${retirementAge + 2} target = base × (1 + ${rate(inflationRate)})^2`,
      feeds: 'The Target (gross) column, and through the drawdown order, every net withdrawal and every shortfall on the page.',
      assumption:
        'This is a fixed-dollar rule, not a percentage of the current balance. A bad stretch of returns does not shrink the withdrawal, which is why a failing plan shows up here as a hard stop rather than a gradual squeeze.'
    },
    {
      id: 'inflationRate',
      kind: 'input',
      label: 'Inflation rate',
      plain:
        'The only thing this grows is the gross withdrawal target. Balances, returns, and the summary cards are all left in the dollars of the year they occur.',
      formula: `target(age) = ${usd.format(baseWithdrawal)} × (1 + ${rate(inflationRate)})^(age − ${retirementAge})\n\nage ${retirementAge}: ${usd.format(baseWithdrawal)}\nage ${lifeExpectancy}: ${usd.format(finalYearTarget)}`,
      feeds: 'The Target (gross) column, and therefore when the portfolio stops being able to keep up.',
      assumption: `Nothing on this page is converted back to today’s dollars. The ${usd.format(endingBalance)} ending balance is an age-${endingAge} number; at ${rate(inflationRate)} inflation it buys roughly what ${usd.format(endingInTodaysDollars)} buys today.`
    },
    {
      id: 'penaltyFreeAge',
      kind: 'input',
      label: 'Penalty-free 401(k) age',
      plain:
        'The age the drawdown order flips. Below it the 401(k) is the last account touched, because it costs income tax plus a 10% penalty. At and above it the 401(k) is drawn first, up to the cap.',
      formula: `before age ${penaltyFreeAge}:\n  taxable → Roth → 401(k) at ${rate(penaltyRate)}\n\nage ${penaltyFreeAge} onward:\n  401(k) up to the cap → taxable\n  → 401(k) above the cap → Roth\n\nyears to bridge: ${bridgeYears === 0 ? 'none — you retire at or after this age' : `ages ${retirementAge} to ${penaltyFreeAge - 1} (${bridgeYears})`}`,
      feeds: 'Which account funds each row, and how much tax the same gross target costs in the early years versus the later ones.',
      assumption:
        'The real threshold is 59½ and has carve-outs — the rule of 55, SEPP/72(t) payments, Roth conversion ladders. This model uses a single whole-year age and applies the 10% penalty to the entire 401(k) withdrawal rather than just the taxable portion.'
    },
    {
      id: 'bracketCapGross',
      kind: 'input',
      label: '401(k) withdrawal cap',
      plain:
        'Once you are past the penalty-free age, this ceilings how much of each year’s target the 401(k) funds before the taxable account is tapped. The 401(k) is only pushed above the cap after taxable runs dry.',
      formula: `first 401(k) slice =\n  min(401(k) balance, cap this year, remaining target)\n\ncap at age ${retirementAge}: ${usd.format(capAtRetirement)}\ncap at age ${penaltyFreeAge}: ${usd.format(capAtPenaltyFreeAge)}`,
      feeds: 'The From 401(k) and From Taxable columns, and the order in which the account lines on the chart fall away.',
      assumption:
        'This model charges the same flat tax rate on 401(k) dollars whether they sit under or over the cap. Moving the cap therefore changes which account funds a year — not the tax paid, the net income, or the total balance. Treat it as a sequencing control, not a bracket optimizer.'
    },
    {
      id: 'bracketCapGrowth',
      kind: 'input',
      label: '401(k) cap growth',
      plain:
        'How fast the cap is indexed. Tax brackets move every year, including the years you are still working, so the cap compounds from your current age rather than from your retirement year.',
      formula: `cap(age) = ${usd.format(bracketCapGross)} × (1 + ${rate(bracketCapGrowth)})^(age − ${currentAge})\n\n${savingYears} saving years later, at age ${retirementAge},\nthe cap has reached ${usd.format(capAtRetirement)}`,
      feeds: 'The size of the first 401(k) slice in every year at or after the penalty-free age.',
      assumption: `The cap you type is read as today’s dollars. Enter the current top of the bracket you want to stay inside — the default ${usd.format(201775)} is the 24% bracket ceiling for a single filer for tax year 2026 — and let this rate index it forward.`
    },
    {
      id: 'initial401k',
      kind: 'input',
      label: 'Initial 401(k)',
      plain:
        'Your starting traditional balance. It compounds like the other two accounts, but it is the only one carrying both a contribution limit and an early-withdrawal penalty.',
      formula: `${usd.format(initial401k)} × ${preGrowthExpression}\n  = ${usd.format(initial401k * preMultiplier)} at age ${retirementAge}\n  (before any contributions are added)`,
      feeds: 'The 401(k) line on the chart, and the first slice of every withdrawal once you pass the penalty-free age.'
    },
    {
      id: 'contribution401k',
      kind: 'input',
      label: '401(k) contribution',
      plain:
        contributionFrequency === 'monthly'
          ? 'Your 401(k) deposit per month, added at the end of each month and compounding from the month after.'
          : 'Your 401(k) deposit per year, credited at the end of each year and compounding from the year after.',
      formula: `${usd.format(contribution401k)} per ${periodLabel} → ${usd.format(entered401kAnnual)} a year\nannual limit ${usd.format(ANNUAL_401K_LIMIT)}${is401kCapped ? `\n  over the limit → reduced to ${usdCents.format(normalizedContribution401k)} per ${periodLabel}` : '\n  under the limit → used as entered'}\n\nyear n deposit = ${usd.format(annual401k)} × (1 + ${rate(contributionIncrease)})^n\n  n counts from age ${currentAge}`,
      feeds: 'The 401(k) normalized contribution card and the 401(k) line on the chart through the saving years.',
      assumption:
        'Monthly timing compounds each deposit through the year, so it ends slightly ahead of the same annual dollars deposited as one year-end lump. Deposits are assumed never to be missed.'
    },
    {
      id: 'initialRoth',
      kind: 'input',
      label: 'Initial Roth',
      plain:
        'Your starting Roth balance. It is the only account whose withdrawals cost nothing in this model, and after the penalty-free age it is drawn last so those dollars compound the longest.',
      formula: `${usd.format(initialRoth)} × ${preGrowthExpression}\n  = ${usd.format(initialRoth * preMultiplier)} at age ${retirementAge}\n  (before any contributions are added)`,
      feeds: 'The Roth line on the chart and the From Roth column, which stays at zero after the penalty-free age until the other two accounts are empty.'
    },
    {
      id: 'contributionRoth',
      kind: 'input',
      label: 'Roth contribution',
      plain: `Your Roth deposit per ${periodLabel}, added on the same schedule as the other two accounts and grown by the same step-up.`,
      formula: `${usd.format(contributionRoth)} per ${periodLabel} → ${usd.format(annualRoth)} a year\nyear n deposit = ${usd.format(annualRoth)} × (1 + ${rate(contributionIncrease)})^n`,
      feeds: 'The Roth balance at retirement — the reserve the last drawdown years lean on once taxable and 401(k) money is gone.',
      assumption:
        'No IRA contribution limit is enforced on this field. Only the 401(k) entry is checked against a limit, so a large number here will be modeled in full even if you could not legally contribute it.'
    },
    {
      id: 'initialTaxable',
      kind: 'input',
      label: 'Initial taxable',
      plain:
        'Your starting brokerage balance. It is the first account drawn in every year before the penalty-free age, and the second one after it.',
      formula: `${usd.format(initialTaxable)} × ${preGrowthExpression}\n  = ${usd.format(initialTaxable * preMultiplier)} at age ${retirementAge}\n  (before any contributions are added)`,
      feeds: 'The taxable line on the chart, and how many early years you can fund before the model has to reach into the penalized 401(k).'
    },
    {
      id: 'contributionTaxable',
      kind: 'input',
      label: 'Taxable contribution',
      plain:
        'Your brokerage deposit per period. This is the account that funds the years between retiring and the penalty-free age, so it largely decides whether an early retirement has to pay the 10% penalty.',
      formula: `${usd.format(contributionTaxable)} per ${periodLabel} → ${usd.format(annualTaxable)} a year\nyear n deposit = ${usd.format(annualTaxable)} × (1 + ${rate(contributionIncrease)})^n\n\n${bridgeYears === 0 ? 'no years to bridge at this retirement age' : `years to cover before age ${penaltyFreeAge}: ${bridgeYears}`}`,
      feeds: 'The From Taxable column, heavily in the years before the penalty-free age and again as the second source after it.'
    },
    {
      id: 'contributionIncrease',
      kind: 'input',
      label: 'Contribution increase',
      plain:
        'One annual step-up applied to all three contributions at once. It compounds from your current age, so your last saving year deposits noticeably more than your first.',
      formula: `deposit(year n) = base × (1 + ${rate(contributionIncrease)})^n,  n = 0…${Math.max(0, savingYears - 1)}\n\n401(k) first saving year: ${usd.format(annual401k)}\n401(k) last saving year:  ${usd.format(lastYear401k)}`,
      feeds: 'Every contribution added during the saving years, and therefore the balance at retirement.',
      assumption: `The ${usd.format(ANNUAL_401K_LIMIT)} limit is applied to the base amount only. The step-up then compounds on top of it, so later-year 401(k) deposits in this model can sit above the limit.`
    },
    {
      id: 'retirementBalance',
      kind: 'result',
      label: 'Balance at retirement',
      plain: `The portfolio at the moment work stops: the balance carried into age ${retirementAge}, before that year’s growth and before the first withdrawal. This is the figure the withdrawal rate is applied to.`,
      formula: `balance entering age ${retirementAge} = ${usd.format(retirementBalance)}\n  × (1 + ${rate(postRetirementGrowth)}) growth\n  − ${usd.format(baseWithdrawal)} first gross withdrawal\n  = ${usd.format(retirementYearEndTotal)}  ← the age-${retirementAge} table row`,
      feeds: `The base gross withdrawal of ${usd.format(baseWithdrawal)}, which every later year’s target is inflated from.`,
      assumption: `The age-${retirementAge} row in the table is a different number because table rows are end-of-year: it already carries a year of growth and the first withdrawal.`
    },
    {
      id: 'lifetimeWithdrawn',
      kind: 'result',
      label: 'Lifetime withdrawn (net)',
      plain:
        'Every net-of-tax dollar the plan hands you, added up across all drawdown years. This is spending money — the tax and any penalty have already come out.',
      formula: `sum over ages ${retirementAge}…${lifeExpectancy} of\n  (from 401(k) + from Roth + from taxable), each net\n  = ${usd.format(lifetimeWithdrawn)}\n\nage ${retirementAge}: ${usd.format(firstRetirementNet)} net on a ${usd.format(baseWithdrawal)} gross target\n  401(k) ${usd.format(firstRetirementFrom401k)} · Roth ${usd.format(firstRetirementFromRoth)} · taxable ${usd.format(firstRetirementFromTaxable)}`,
      feeds: 'Nothing downstream — it is the headline outcome of the drawdown order.',
      assumption: `Net income moves around from year to year even though the gross target is smooth, because a year funded from Roth loses nothing to tax while a 401(k) year before age ${penaltyFreeAge} loses ${rate(penaltyRate)}.`
    },
    {
      id: 'lifetimeShortfall',
      kind: 'result',
      label: 'Lifetime shortfall',
      plain:
        'Gross target the three accounts could not cover, added up over the whole retirement. Anything above zero means the money ran out before the last row.',
      formula: `per year: shortfall = target − gross actually taken\nsummed over ages ${retirementAge}…${lifeExpectancy} = ${usd.format(lifetimeShortfall)}\n\n${depletionAge === null ? 'every year’s target was funded in full' : `first year not fully funded: age ${depletionAge}`}`,
      feeds: 'The direction arrow on this card. A non-zero value is this model’s failure signal.',
      assumption:
        'This figure is gross while the Lifetime withdrawn card above it is net, so the two do not add up to the lifetime target. It also counts every year after the money runs out at the full inflated target, so it climbs quickly once depletion starts.'
    },
    {
      id: 'endingBalance',
      kind: 'result',
      label: 'Ending balance',
      plain: `What is left at age ${endingAge} after the final withdrawal. Zero means the plan ran out; a very large number means it was more conservative than it needed to be.`,
      formula: `age ${endingAge}: 401(k) ${usd.format(endingBalances[0])}\n         Roth ${usd.format(endingBalances[1])}\n      taxable ${usd.format(endingBalances[2])}\n        total ${usd.format(endingBalance)}\n\nfinal year gross target ${usd.format(finalYearTarget)}`,
      feeds: 'Nothing — it is the last row of the projection.',
      assumption:
        'No required minimum distributions, no estate or legacy rule, and no spend-down of a house or other asset. The model simply stops at this age with whatever is left.'
    },
    {
      id: 'normalizedContribution401k',
      kind: 'result',
      label: '401(k) normalized contribution',
      plain: `Your 401(k) deposit after the annual limit is applied, shown in the same period you entered it. If your entry is over the limit, the model quietly lowers it and this card is where that shows up.`,
      formula: is401kCapped
        ? `${usd.format(contribution401k)} × ${periodsPerYear} = ${usd.format(entered401kAnnual)} a year\nover the ${usd.format(ANNUAL_401K_LIMIT)} limit\n  → ${usd.format(ANNUAL_401K_LIMIT)} / ${periodsPerYear} = ${usdCents.format(normalizedContribution401k)} per ${periodLabel}`
        : `${usd.format(contribution401k)} × ${periodsPerYear} = ${usd.format(entered401kAnnual)} a year\nunder the ${usd.format(ANNUAL_401K_LIMIT)} limit\n  → unchanged at ${usdCents.format(normalizedContribution401k)} per ${periodLabel}`,
      feeds: 'Every 401(k) deposit in the saving years. The Roth and taxable contributions are not checked against any limit.',
      assumption: `One flat limit, with no age-50 catch-up and no employer match. The contribution step-up is applied after this limit, so deposits in later saving years can exceed ${usd.format(ANNUAL_401K_LIMIT)}.`
    }
  ];
}
